import type { TFunction } from 'i18next';
import {
  getErrorCode,
  getErrorResponse,
  getErrorStatus,
} from 'app/utils/apiError';

export type InvitationErrorSource = 'pending' | 'member';

const getErrorLimit = (error: unknown): number | undefined => {
  const data = getErrorResponse(error)?.data as
    | { error_data?: { limit?: unknown } }
    | undefined;
  const limit = data?.error_data?.limit;

  return typeof limit === 'number' ? limit : undefined;
};

const messageForCode = (
  code: string,
  error: unknown,
  t: TFunction,
  source: InvitationErrorSource,
): string | undefined => {
  switch (code) {
    case 'ALREADY_MEMBER':
      return t('inviteUserModal.memberAlreadyExists');
    case 'INVITE_LINK_UNAVAILABLE':
      return source === 'member'
        ? t('usersManagement.inviteErrors.linkUnavailableMember')
        : t('usersManagement.inviteErrors.linkUnavailable');
    case 'INVITEE_NOT_FOUND':
      return t('usersManagement.inviteErrors.notFound');
    case 'INVITE_LINK_FORBIDDEN':
      return t('usersManagement.inviteErrors.forbidden');
    case 'INVITEE_BLOCKED':
      return t('usersManagement.inviteErrors.blocked');
    case 'IDENTITY_SERVICE_UNAVAILABLE':
      return t('usersManagement.inviteErrors.identityUnavailable');
    case 'INVITATION_DELIVERY_UNAVAILABLE':
      return t('usersManagement.inviteErrors.deliveryUnavailable');
    case 'INVITATION_EMAIL_SUPPRESSED':
      return t('usersManagement.inviteErrors.emailSuppressed');
    case 'TRIAL_INVITATION_LIMIT_REACHED': {
      const limit = getErrorLimit(error);

      return limit === undefined
        ? t('usersManagement.inviteErrors.tooManyRequests')
        : t('usersManagement.inviteErrors.trialLimit', { limit });
    }
    default:
      return undefined;
  }
};

const messageForStatus = (
  status: number | undefined,
  t: TFunction,
): string | undefined => {
  switch (status) {
    case 404:
      return t('usersManagement.inviteErrors.notFound');
    case 403:
      return t('usersManagement.inviteErrors.forbidden');
    case 429:
      return t('usersManagement.inviteErrors.tooManyRequests');
    default:
      return undefined;
  }
};

export const invitationErrorMessage = (
  error: unknown,
  t: TFunction,
  source: InvitationErrorSource = 'pending',
  fallbackKey = 'somethingWentWrong',
): string => {
  const code = getErrorCode(error);
  const message =
    code === undefined
      ? messageForStatus(getErrorStatus(error), t)
      : messageForCode(code, error, t, source);

  return message ?? t(fallbackKey);
};

export const isStaleInvitationLinkError = (error: unknown): boolean =>
  getErrorCode(error) === 'INVITE_LINK_UNAVAILABLE' ||
  getErrorStatus(error) === 404;
